import React from "react";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { PAGE_NAME, ROUTE } from "../../../../../constants/constants";
import { Loader } from "../../../../atoms/loader";
import {
    useUpdateSleetProjectMutation,
    useGetSleetProjectQuery,
} from "../../../../../store/credentialsManagement/credentialsSlice";
import { Dropdown } from "../../../../atoms/dropdown";
import { payloadType, ResponseType } from "../../../../../types";
import { Input } from "../../../../atoms/input";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserListQuery } from "../../../../../store/user/userSlice";

export const SleetProjectEdit = () => {
    const pageClassName = "p-sleetProjectEdit";

    const { id } = useLocation().state as { id: string };

    const navigate = useNavigate();

    const { data: users, isLoading: userListLoading } = useUserListQuery({});
    const [updateAPI, { isLoading }] = useUpdateSleetProjectMutation();
    const { data: project, isSuccess: projectSuccess } = useGetSleetProjectQuery(
        id as string,
        {
            skip: id ? false : true,
        }
    );

    React.useEffect(() => {
        if (projectSuccess) {
            setPayload((prevState) => ({
                ...prevState,
                name: {
                    ...prevState.name,
                    value: project?.result?.name,
                },
                secret_name: {
                    ...prevState.secret_name,
                    value: project?.result?.secretName,
                },
                production_feed_url: {
                    ...prevState.production_feed_url,
                    value: project?.result?.productionFeedUrl,
                },
                development_feed_url: {
                    ...prevState.development_feed_url,
                    value: project?.result?.developmentFeedUrl,
                },
                created_by: {
                    ...prevState.created_by,
                    value: {
                        label:
                            project?.result?.createdBy?.firstName +
                            " " +
                            project?.result?.createdBy?.lastName,
                        value: project?.result?.createdBy?.id,
                    },
                },
            }));
        }
    }, [projectSuccess, project, users]);

    const initialPayload: payloadType = {
        name: {
            value: project?.result?.name,
            error: "",
            isError: false,
        },
        secret_name: {
            value: project?.result?.secretName,
            error: "",
            isError: false,
        },
        production_feed_url: {
            value: project?.result?.productionFeedUrl,
            error: "",
            isError: false,
        },
        development_feed_url: {
            value: project?.result?.developmentFeedUrl,
            error: "",
            isError: false,
        },
        created_by: {
            value: {
                label: project?.result?.createdBy?.firstName + " " + project?.result?.createdBy?.lastName,
                value: project?.result?.createdBy?.id,
            },
            error: "",
            isError: false,
        },
    };

    const [payload, setPayload] = React.useState(initialPayload);

    const submitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData();
        const toastId = toast.loading("Loading...");

        formData.append("name", payload.name.value as string);
        formData.append("secret_name", payload.secret_name.value as string);
        formData.append(
            "production_feed_url",
            payload.production_feed_url.value as string
        );
        formData.append(
            "development_feed_url",
            payload.development_feed_url.value as string
        );
        formData.append(
            "created_by",
            (payload.created_by.value as { label: string; value: string })?.value
        );
        formData.append("_method", "PUT");

        const response = (await updateAPI({
            id: id as unknown as string,
            formData: formData,
        })) as ResponseType;

        if (response?.data?.success) {
            setPayload(initialPayload);

            toast.update(toastId, {
                render: response?.data?.message,
                type: toast.TYPE.SUCCESS,
                isLoading: isLoading,
                autoClose: 2000,
            });
            navigate(ROUTE.SLEET_PROJECT_LIST);
        } else {
            toast.update(toastId, {
                render: response?.error?.data?.message,
                type: toast.TYPE.ERROR,
                isLoading: isLoading,
                autoClose: 2000,
            });

            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                });
            } else {
                toast.error(response?.error?.message);
            }
        }
    };
    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>
                {PAGE_NAME.EDIT_SLEET_PROJECT}
            </p>
            <Loader isLoading={isLoading || userListLoading}>
                <form className={`${pageClassName}__form`} onSubmit={submitHandler}>
                    <div className={`${pageClassName}__form__container`}>
                        <Input
                            label="Project Name"
                            placeholder="Project Name"
                            value={payload.name?.value as string}
                            onChange={(e) =>
                                setPayload({
                                    ...payload,
                                    name: {
                                        value: e.target.value,
                                        error: "",
                                        isError: false,
                                    },
                                })
                            }
                            isError={payload.name.isError}
                            error={payload.name.error}
                            readonly
                        />

                        <Input
                            label="Secret Name"
                            placeholder="Secret Name"
                            value={payload.secret_name?.value as string}
                            onChange={(e) =>
                                setPayload({
                                    ...payload,
                                    secret_name: {
                                        value: e.target.value,
                                        error: "",
                                        isError: false,
                                    },
                                })
                            }
                            isError={payload.secret_name.isError}
                            error={payload.secret_name.error}
                        />

                        <Input
                            label="Production Feed URL"
                            placeholder="Production Feed URL"
                            value={payload.production_feed_url?.value as string}
                            onChange={(e) =>
                                setPayload({
                                    ...payload,
                                    production_feed_url: {
                                        value: e.target.value,
                                        error: "",
                                        isError: false,
                                    },
                                })
                            }
                            isError={payload.production_feed_url.isError}
                            error={payload.production_feed_url.error}
                        />

                        <Input
                            label="Development Feed URL"
                            placeholder="Development Feed URL"
                            value={payload.development_feed_url?.value as string}
                            onChange={(e) =>
                                setPayload({
                                    ...payload,
                                    development_feed_url: {
                                        value: e.target.value,
                                        error: "",
                                        isError: false,
                                    },
                                })
                            }
                            isError={payload.development_feed_url.isError}
                            error={payload.development_feed_url.error}
                        />

                        <div className={`${pageClassName}__form__group`}>
                            <label className={`${pageClassName}__form__group__label`}>
                                Requested By
                            </label>
                            <Dropdown
                                options={users?.result?.data.map((user: any) => ({
                                    label: user?.firstName + " " + user?.lastName,
                                    value: user?.id,
                                }))}
                                placeholder="Please select a requested User"
                                selected={
                                    payload.created_by.value as { label: string; value: string }
                                }
                                onClickChange={(e) =>
                                    setPayload({
                                        ...payload,
                                        created_by: {
                                            value: e as { label: string; value: string },
                                            error: "",
                                            isError: false,
                                        },
                                    })
                                }
                                isBordered
                                isError={payload.created_by.isError}
                                error={payload.created_by.error}
                            />
                        </div>
                    </div>
                    <div className={`${pageClassName}__form__action`}>
                        <button
                            className={`${pageClassName}__form__action__button`}
                            type="submit"
                        >
                            Save
                        </button>
                        <button
                            className={`${pageClassName}__form__action__cancel`}
                            type="button"
                            onClick={() => {
                                setPayload(initialPayload);
                                navigate(ROUTE.SLEET_PROJECT_LIST);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Loader>
        </div>
    );
};
